/** Map raw FIDS API rows → homepage board shape (mirrors PHP flights-data.php). */

const AIRLINE_NAMES = {
  L6: 'Mauritania Airlines',
  AT: 'Royal Air Maroc',
  AF: 'Air France',
  TK: 'Turkish Airlines',
  QR: 'Qatar Airways',
  ET: 'Ethiopian Airlines',
  TP: 'TAP Air Portugal',
  IB: 'Iberia',
  SN: 'Brussels Airlines',
  AH: 'Air Algérie',
  TU: 'Tunisair',
  HV: 'Transavia',
  VY: 'Vueling',
  W6: 'Wizz Air',
  BA: 'British Airways',
  LH: 'Lufthansa',
  KL: 'KLM',
  EK: 'Emirates',
  EY: 'Etihad Airways',
  MS: 'EgyptAir',
};

const STATUS_KEY_MAP = {
  'on time': 'ontime',
  ontime: 'ontime',
  scheduled: 'scheduled',
  boarding: 'boarding',
  'gate open': 'gate',
  'final call': 'finalcall',
  finalcall: 'finalcall',
  approaching: 'approaching',
  baggage: 'baggage',
  landed: 'landed',
  arrived: 'arrived',
  delayed: 'delayed',
  'new time': 'newtime',
  departed: 'departed',
  cancelled: 'cancelled',
  canceled: 'cancelled',
};

export function flightsApiBase() {
  // Prefer same-origin /api when served via Vite proxy or Node (api/public + /api)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return (import.meta.env.VITE_FLIGHTS_API_BASE || '/api/flights').replace(/\/$/, '');
    }
  }
  return (import.meta.env.VITE_FLIGHTS_API_BASE || 'https://nouakchottaeroport.com/api/flights').replace(
    /\/$/,
    ''
  );
}

function friendlyAirlineName(code) {
  const c = String(code || '').toUpperCase().trim();
  return AIRLINE_NAMES[c] || (c || 'Airline');
}

function formatTime(datetime) {
  if (!datetime || datetime === '0000-00-00 00:00:00') return '—';
  const normalized = String(datetime).includes('T')
    ? String(datetime)
    : `${String(datetime).replace(' ', 'T')}Z`;
  const ts = Date.parse(normalized);
  if (Number.isNaN(ts)) {
    const m = String(datetime).match(/(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '—';
  }
  return new Date(ts).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
}

function displayName(name, code) {
  const n = String(name || '').trim();
  if (n) {
    // Keep Arabic / non-Latin scripts as provided by the API
    if (/[\u0600-\u06FF]/.test(n)) return n;
    return n
      .toLowerCase()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  const c = String(code || '').toUpperCase().trim();
  return c || '—';
}

function pickLocalized(en, fr, ar) {
  return {
    en: String(en ?? '').trim(),
    fr: String(fr ?? '').trim(),
    ar: String(ar ?? '').trim(),
  };
}

function formatStatusLabel(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/[\u0600-\u06FF]/.test(s)) return s;
  return s.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function statusKey(status, paxStatus) {
  const raw = String(paxStatus || status || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
  if (STATUS_KEY_MAP[raw]) return STATUS_KEY_MAP[raw];
  if (raw.includes('cancel')) return 'cancelled';
  if (raw.includes('delay')) return 'delayed';
  if (raw.includes('board')) return 'boarding';
  if (raw.includes('land')) return 'landed';
  if (raw.includes('depart')) return 'departed';
  if (raw.includes('arriv')) return 'arrived';
  return 'scheduled';
}

function dash(v) {
  const s = String(v ?? '').trim();
  return s || '—';
}

/** @param {object} row API flight row */
export function mapFlightRow(row) {
  const isArrival = (row?.direction || '') === 'arrival';
  let code = String(row?.airline_code || '')
    .toUpperCase()
    .trim();
  if (!code && row?.flight_number) {
    code = String(row.flight_number).trim().split(/\s+/)[0]?.toUpperCase() || '';
  }

  let airlineName = String(row?.airline_name || '').trim();
  airlineName = airlineName ? displayName(airlineName, code) : friendlyAirlineName(code);

  const scheduled = formatTime(row?.scheduled_time);
  const actual = formatTime(row?.actual_time);
  let estimated = formatTime(row?.estimated_time);
  if (estimated === '—') estimated = actual;
  if (estimated === '—') estimated = scheduled;

  const airportCode = String(row?.airport_code || '')
    .toUpperCase()
    .trim();
  const airportNames = pickLocalized(row?.airport_name, row?.airport_name_fr, row?.airport_name_ar);
  const airportNameEn = displayName(airportNames.en, airportCode);
  const airportNameFr = displayName(airportNames.fr || airportNames.en, airportCode);
  const airportNameAr = airportNames.ar || airportNameEn;

  const statusI18n = pickLocalized(row?.status, row?.status_fr, row?.status_ar);
  const paxI18n = pickLocalized(row?.pax_status, row?.pax_status_fr, row?.pax_status_ar);
  // Prefer passenger-facing status when present
  const statusEn = formatStatusLabel(paxI18n.en || statusI18n.en) || 'Scheduled';
  const statusFr = formatStatusLabel(paxI18n.fr || statusI18n.fr || paxI18n.en || statusI18n.en) || statusEn;
  const statusAr = formatStatusLabel(paxI18n.ar || statusI18n.ar || paxI18n.en || statusI18n.en) || statusEn;

  let date = row?.flight_date || '';
  if (!date && row?.scheduled_time) {
    date = String(row.scheduled_time).slice(0, 10);
  }

  const base = {
    id: row?.id ?? null,
    flight: String(row?.flight_number || '').trim(),
    airline: airlineName,
    airlineCode: code.toLowerCase(),
    logoUrl: String(row?.airline_logo_url || '').trim(),
    status: statusEn,
    statusEn,
    statusFr,
    statusAr,
    statusKey: statusKey(row?.status, row?.pax_status),
    date: String(date),
    terminal: dash(row?.terminal),
    gate: dash(row?.gate),
    baggage: dash(row?.belt),
    checkin: dash(row?.terminal),
    lastSyncedAt: row?.last_synced_at || null,
    scheduledRaw: row?.scheduled_time || null,
    actualRaw: row?.actual_time || null,
    estimatedRaw: row?.estimated_time || null,
  };

  if (isArrival) {
    return {
      ...base,
      origin: airportNameEn,
      originFr: airportNameFr,
      originAr: airportNameAr,
      originCode: airportCode || '—',
      sta: scheduled,
      eta: estimated,
      ata: actual,
    };
  }

  return {
    ...base,
    destination: airportNameEn,
    destinationFr: airportNameFr,
    destinationAr: airportNameAr,
    destinationCode: airportCode || '—',
    std: scheduled,
    etd: estimated,
    atd: actual,
  };
}

function sortByTime(a, b) {
  const ta = a.sta || a.std || '';
  const tb = b.sta || b.std || '';
  return String(ta).localeCompare(String(tb));
}

/**
 * Fetch arrivals or departures from the public FIDS API.
 * @param {'arrivals'|'departures'} path
 */
export async function fetchFlights(path) {
  const url = `${flightsApiBase()}/${path}`;
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Flights API ${res.status}`);
  }
  const data = await res.json();
  if (!data?.ok || !Array.isArray(data.flights)) {
    return [];
  }

  return data.flights
    .filter((row) => row && (row.is_active === undefined || Number(row.is_active) !== 0))
    .map(mapFlightRow)
    .sort(sortByTime);
}

export function formatUpdatedLabel(flights) {
  let latest = null;
  for (const f of flights) {
    if (!f?.lastSyncedAt) continue;
    const ts = Date.parse(String(f.lastSyncedAt).includes('T') ? f.lastSyncedAt : `${f.lastSyncedAt}Z`);
    if (!Number.isNaN(ts) && (latest === null || ts > latest)) latest = ts;
  }
  const d = latest !== null ? new Date(latest) : new Date();
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${time} GMT`;
}
